import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BadgeCheck, BookOpen, Briefcase, Users } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function DetailContent() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [f, setF] = useState(null);

    useEffect(() => {
        api.get(`/faculty/${id}`).then((r) => setF(r.data)).catch(() => {});
    }, [id]);

    if (!f) return <p className="text-sm text-muted-foreground">Loading...</p>;

    return (
        <div className="mx-auto max-w-5xl" data-testid="faculty-detail">
            <button onClick={() => navigate(-1)} data-testid="back-button" className="mb-6 flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors duration-200 hover:text-primary">
                <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <div className="card-soft p-8">
                <div className="flex flex-wrap items-center gap-6">
                    <img src={f.picture} alt={f.name} className="h-24 w-24 rounded-full object-cover" />
                    <div>
                        <h1 className="font-display text-3xl font-bold tracking-tight">{f.name}</h1>
                        <p className="mt-1 text-sm text-muted-foreground">{f.profile?.title} · {f.profile?.institution}</p>
                        {f.verified && (
                            <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-teal-100 px-3 py-1 text-xs font-bold text-teal-800" data-testid="faculty-verified-badge">
                                <BadgeCheck className="h-4 w-4" /> Verified Faculty
                            </p>
                        )}
                    </div>
                </div>
                {f.profile?.bio && <p className="mt-6 text-sm leading-relaxed text-muted-foreground">{f.profile.bio}</p>}
                <div className="mt-6 flex flex-wrap gap-2">
                    {(f.profile?.research_areas || []).map((a) => (
                        <span key={a} className="rounded-full bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary">{a}</span>
                    ))}
                </div>
                <div className="mt-8 grid gap-4 sm:grid-cols-3">
                    <div className="rounded-xl bg-muted p-5 text-center">
                        <BookOpen className="mx-auto h-5 w-5 text-primary" />
                        <p className="mt-2 font-display text-2xl font-bold">{f.projects?.length || 0}</p>
                        <p className="text-xs text-muted-foreground">Active Projects</p>
                    </div>
                    <div className="rounded-xl bg-muted p-5 text-center">
                        <Briefcase className="mx-auto h-5 w-5 text-primary" />
                        <p className="mt-2 font-display text-2xl font-bold">{f.internships?.length || 0}</p>
                        <p className="text-xs text-muted-foreground">Internship Programs</p>
                    </div>
                    <div className="rounded-xl bg-muted p-5 text-center">
                        <Users className="mx-auto h-5 w-5 text-primary" />
                        <p className="mt-2 font-display text-2xl font-bold">{f.profile?.mentorship_available ? "Open" : "Full"}</p>
                        <p className="text-xs text-muted-foreground">Mentorship</p>
                    </div>
                </div>
            </div>

            <h2 className="mt-12 font-display text-xl font-semibold">Internship Programs</h2>
            <div className="mt-4 grid gap-5 md:grid-cols-2">
                {f.internships?.length === 0 && <p className="text-sm text-muted-foreground">No open programs right now.</p>}
                {f.internships?.map((it) => (
                    <Link key={it.id} to={`/internships/${it.id}`} data-testid={`faculty-internship-${it.id}`} className="card-soft block p-6 transition-transform duration-200 hover:-translate-y-1">
                        <p className="font-display text-base font-semibold">{it.title}</p>
                        <p className="mt-1 text-xs text-muted-foreground">{it.duration_months} months · ${it.fee_usd} · {it.mode}</p>
                    </Link>
                ))}
            </div>

            <h2 className="mt-12 font-display text-xl font-semibold">Research Projects</h2>
            <div className="mt-4 grid gap-5 md:grid-cols-2">
                {f.projects?.length === 0 && <p className="text-sm text-muted-foreground">No active projects right now.</p>}
                {f.projects?.map((p) => (
                    <div key={p.id} className="card-soft p-6" data-testid={`faculty-project-${p.id}`}>
                        <p className="font-display text-base font-semibold">{p.title}</p>
                        <p className="mt-1 text-xs text-muted-foreground">{p.domain} · {p.duration} · {p.difficulty}</p>
                        <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{p.description}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function FacultyDetail() {
    const { user } = useAuth();
    if (user) return <DashboardLayout title="Faculty Profile"><DetailContent /></DashboardLayout>;
    return (
        <div className="bg-background">
            <Header />
            <div className="px-6 py-14"><DetailContent /></div>
            <Footer />
        </div>
    );
}
